import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { reports } from "../services/api";
import type {
  ReportSummary,
  ReportInvoiceBreakdownItem,
  ZoneSummary,
  HaulierReportLine,
} from "../types";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

function formatDate(iso: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** YYYY-MM-DD from a Date object */
function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

// ─── CSV Export ──────────────────────────────────────────────────────────────

function escapeField(value: string | number | null | undefined): string {
  const str = value == null ? "" : String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function toCsvRow(fields: (string | number | null | undefined)[]): string {
  return fields.map(escapeField).join(",");
}

function exportReportCsv(data: ReportSummary): void {
  const sections: string[] = [];

  // Header
  sections.push("PERIOD OVERCHARGE REPORT");
  sections.push(
    toCsvRow(["Period", `${formatDate(data.period.from)} - ${formatDate(data.period.to)}`]),
  );
  sections.push(toCsvRow(["Total Invoices", data.invoice_count]));
  sections.push(toCsvRow(["Total Lines Checked", data.total_lines]));
  sections.push(
    toCsvRow(["Total Invoiced", formatCurrency(data.total_invoiced)]),
  );
  sections.push(
    toCsvRow(["Total Expected", formatCurrency(data.total_expected)]),
  );
  sections.push(
    toCsvRow(["Total Difference", formatCurrency(data.total_difference)]),
  );
  sections.push("");

  // Invoice breakdown
  sections.push("INVOICE BREAKDOWN");
  sections.push(
    toCsvRow([
      "Invoice #",
      "Supplier",
      "Date",
      "Type",
      "Invoiced",
      "Expected",
      "Difference",
    ]),
  );
  for (const inv of data.invoice_breakdown) {
    sections.push(
      toCsvRow([
        inv.invoice_number,
        inv.supplier,
        inv.invoice_date,
        inv.invoice_type,
        inv.total_invoiced.toFixed(2),
        inv.total_expected.toFixed(2),
        inv.difference.toFixed(2),
      ]),
    );
  }
  sections.push("");

  // Zone breakdown
  if (data.zone_breakdown.length > 0) {
    sections.push("ZONE BREAKDOWN");
    sections.push(
      toCsvRow([
        "Zone",
        "Pallet Type",
        "Count",
        "Invoiced",
        "Expected",
        "Difference",
        "Per Unit Difference",
      ]),
    );
    for (const zone of data.zone_breakdown) {
      for (const pb of zone.pallet_breakdown) {
        sections.push(
          toCsvRow([
            zone.zone,
            pb.pallet_type,
            pb.count,
            pb.total_invoiced.toFixed(2),
            pb.total_expected.toFixed(2),
            pb.difference.toFixed(2),
            pb.per_unit_difference.toFixed(2),
          ]),
        );
      }
      sections.push(
        toCsvRow([
          `Zone ${zone.zone} Total`,
          "",
          zone.lines,
          zone.total_invoiced.toFixed(2),
          zone.total_expected.toFixed(2),
          zone.difference.toFixed(2),
          "",
        ]),
      );
    }
  }

  const BOM = "\uFEFF";
  const csv = BOM + sections.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `overcharge_report_${data.period.from}_${data.period.to}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Quick-range helpers ─────────────────────────────────────────────────────

type RangePreset =
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "year_to_date";

function presetRange(preset: RangePreset): { from: string; to: string } {
  const now = new Date();
  switch (preset) {
    case "this_month":
      return {
        from: toDateStr(startOfMonth(now)),
        to: toDateStr(endOfMonth(now)),
      };
    case "last_month": {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        from: toDateStr(startOfMonth(prev)),
        to: toDateStr(endOfMonth(prev)),
      };
    }
    case "last_3_months": {
      const three = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      return {
        from: toDateStr(startOfMonth(three)),
        to: toDateStr(endOfMonth(now)),
      };
    }
    case "last_6_months": {
      const six = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      return {
        from: toDateStr(startOfMonth(six)),
        to: toDateStr(endOfMonth(now)),
      };
    }
    case "year_to_date":
      return {
        from: `${now.getFullYear()}-01-01`,
        to: toDateStr(now),
      };
  }
}

// ─── Report tab type ─────────────────────────────────────────────────────────

type ReportTab = "overcharge" | "haulier_pricing";

// ─── Sort helpers ───────────────────────────────────────────────────────────

type SortKey = keyof Pick<
  ReportInvoiceBreakdownItem,
  "invoice_number" | "supplier" | "invoice_date" | "total_invoiced" | "total_expected" | "difference"
>;

function compareInvoices(
  a: ReportInvoiceBreakdownItem,
  b: ReportInvoiceBreakdownItem,
  key: SortKey,
  asc: boolean,
): number {
  let cmp: number;
  const av = a[key];
  const bv = b[key];
  if (typeof av === "number" && typeof bv === "number") {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv));
  }
  return asc ? cmp : -cmp;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>("overcharge");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="mt-1 text-sm text-gray-600">
          Generate overcharge summaries and haulier pricing reports.
        </p>
      </div>

      {/* ── Tab Switcher ──────────────────────────────────────────────── */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {(
            [
              ["overcharge", "Overcharge Summary"],
              ["haulier_pricing", "Haulier Pricing Report"],
            ] as [ReportTab, string][]
          ).map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "border-emerald-500 text-emerald-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "overcharge" && <OverchargeSummaryTab />}
      {activeTab === "haulier_pricing" && <HaulierPricingTab />}
    </div>
  );
}

// ─── Overcharge Summary Tab ──────────────────────────────────────────────────

function OverchargeSummaryTab() {
  const now = new Date();
  const [fromDate, setFromDate] = useState(
    toDateStr(startOfMonth(now)),
  );
  const [toDate, setToDate] = useState(
    toDateStr(endOfMonth(now)),
  );

  // The "active" query params — only updated on Generate click
  const [activeFrom, setActiveFrom] = useState("");
  const [activeTo, setActiveTo] = useState("");

  const [sortKey, setSortKey] = useState<SortKey>("invoice_date");
  const [sortAsc, setSortAsc] = useState(false);

  const enabled = !!activeFrom && !!activeTo;

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<ReportSummary>({
    queryKey: ["reports", "summary", activeFrom, activeTo],
    queryFn: () => reports.summary(activeFrom, activeTo),
    enabled,
  });

  const handleGenerate = useCallback(() => {
    setActiveFrom(fromDate);
    setActiveTo(toDate);
  }, [fromDate, toDate]);

  const handlePreset = useCallback(
    (preset: RangePreset) => {
      const range = presetRange(preset);
      setFromDate(range.from);
      setToDate(range.to);
      setActiveFrom(range.from);
      setActiveTo(range.to);
    },
    [],
  );

  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortAsc(!sortAsc);
      } else {
        setSortKey(key);
        setSortAsc(true);
      }
    },
    [sortKey, sortAsc],
  );

  const sortedInvoices = useMemo(() => {
    if (!data) return [];
    return [...data.invoice_breakdown].sort((a, b) =>
      compareInvoices(a, b, sortKey, sortAsc),
    );
  }, [data, sortKey, sortAsc]);

  return (
    <div className="space-y-6">
      {/* ── Date Range Selector ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Select Period
        </h2>

        {/* Quick preset buttons */}
        <div className="mb-4 flex flex-wrap gap-2">
          {(
            [
              ["this_month", "This Month"],
              ["last_month", "Last Month"],
              ["last_3_months", "Last 3 Months"],
              ["last_6_months", "Last 6 Months"],
              ["year_to_date", "Year to Date"],
            ] as [RangePreset, string][]
          ).map(([preset, label]) => (
            <button
              key={preset}
              onClick={() => handlePreset(preset)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {label}
            </button>
          ))}
        </div>

        {/* Custom date range */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label
              htmlFor="from-date"
              className="block text-sm font-medium text-gray-700"
            >
              From
            </label>
            <input
              id="from-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label
              htmlFor="to-date"
              className="block text-sm font-medium text-gray-700"
            >
              To
            </label>
            <input
              id="to-date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <button
            onClick={handleGenerate}
            disabled={!fromDate || !toDate}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Generate Report
          </button>
        </div>
      </div>

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <svg
            className="h-6 w-6 animate-spin text-emerald-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="ml-2 text-sm text-gray-500">
            Generating report...
          </span>
        </div>
      )}

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to generate report.{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {/* ── Report Results ───────────────────────────────────────────────── */}
      {data && <ReportResults data={data} sortedInvoices={sortedInvoices} sortKey={sortKey} sortAsc={sortAsc} onSort={handleSort} />}
    </div>
  );
}

// ─── Report Results Sub-Component ───────────────────────────────────────────

function ReportResults({
  data,
  sortedInvoices,
  sortKey,
  sortAsc,
  onSort,
}: {
  data: ReportSummary;
  sortedInvoices: ReportInvoiceBreakdownItem[];
  sortKey: SortKey;
  sortAsc: boolean;
  onSort: (key: SortKey) => void;
}) {
  return (
    <div className="space-y-6">
      {/* ── Header Card ─────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Period Summary
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {formatDate(data.period.from)} &ndash;{" "}
              {formatDate(data.period.to)}
            </p>
          </div>
          <button
            onClick={() => exportReportCsv(data)}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
            Export CSV
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <MiniStat label="Invoices" value={String(data.invoice_count)} />
          <MiniStat label="Lines Checked" value={data.total_lines.toLocaleString()} />
          <MiniStat
            label="Total Invoiced"
            value={formatCurrency(data.total_invoiced)}
            mono
          />
          <MiniStat
            label="Total Expected"
            value={formatCurrency(data.total_expected)}
            mono
          />
          <MiniStat
            label="Total Overcharge"
            value={formatCurrency(data.total_difference)}
            mono
            highlight={data.total_difference > 0}
          />
        </div>
      </div>

      {/* ── Invoice Breakdown Table ─────────────────────────────────── */}
      {sortedInvoices.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Invoice Breakdown
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <SortableHeader
                    label="Invoice #"
                    sortKey="invoice_number"
                    currentKey={sortKey}
                    asc={sortAsc}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Supplier"
                    sortKey="supplier"
                    currentKey={sortKey}
                    asc={sortAsc}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Date"
                    sortKey="invoice_date"
                    currentKey={sortKey}
                    asc={sortAsc}
                    onSort={onSort}
                  />
                  <th className="px-6 py-3">Type</th>
                  <SortableHeader
                    label="Invoiced"
                    sortKey="total_invoiced"
                    currentKey={sortKey}
                    asc={sortAsc}
                    onSort={onSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Expected"
                    sortKey="total_expected"
                    currentKey={sortKey}
                    asc={sortAsc}
                    onSort={onSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Difference"
                    sortKey="difference"
                    currentKey={sortKey}
                    asc={sortAsc}
                    onSort={onSort}
                    align="right"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-3 font-medium text-gray-900">
                      {inv.invoice_number || "-"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.supplier}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {formatDate(inv.invoice_date)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          inv.invoice_type === "HAULIER"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {inv.invoice_type === "HAULIER"
                          ? "Haulier"
                          : "Warehouse"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-right font-mono text-gray-900">
                      {formatCurrency(inv.total_invoiced)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-right font-mono text-gray-900">
                      {formatCurrency(inv.total_expected)}
                    </td>
                    <td
                      className={`whitespace-nowrap px-6 py-3 text-right font-mono font-semibold ${
                        Math.abs(inv.difference) >= 0.01
                          ? inv.difference > 0
                            ? "text-red-600"
                            : "text-green-600"
                          : "text-green-600"
                      }`}
                    >
                      {Math.abs(inv.difference) < 0.01
                        ? "Match"
                        : formatCurrency(inv.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-300 bg-gray-50 font-semibold text-gray-900">
                  <td className="px-6 py-3" colSpan={4}>
                    Total
                  </td>
                  <td className="px-6 py-3 text-right font-mono">
                    {formatCurrency(
                      sortedInvoices.reduce(
                        (s, i) => s + i.total_invoiced,
                        0,
                      ),
                    )}
                  </td>
                  <td className="px-6 py-3 text-right font-mono">
                    {formatCurrency(
                      sortedInvoices.reduce(
                        (s, i) => s + i.total_expected,
                        0,
                      ),
                    )}
                  </td>
                  <td
                    className={`px-6 py-3 text-right font-mono ${
                      sortedInvoices.reduce((s, i) => s + i.difference, 0) > 0
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {formatCurrency(
                      sortedInvoices.reduce(
                        (s, i) => s + i.difference,
                        0,
                      ),
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Zone Breakdown Table ────────────────────────────────────── */}
      {data.zone_breakdown.length > 0 && (
        <ZoneBreakdownSection zones={data.zone_breakdown} />
      )}

      {/* ── Empty State ─────────────────────────────────────────────── */}
      {data.invoice_count === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-500">
            No verified invoices found in this period.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function MiniStat({
  label,
  value,
  mono = false,
  highlight = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p
        className={`mt-1 text-lg font-semibold ${
          highlight ? "text-red-600" : "text-gray-900"
        } ${mono ? "font-mono" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}

function SortableHeader({
  label,
  sortKey: sk,
  currentKey,
  asc,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  asc: boolean;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const isActive = sk === currentKey;
  return (
    <th
      className={`cursor-pointer select-none px-6 py-3 transition-colors hover:text-gray-700 ${
        align === "right" ? "text-right" : ""
      }`}
      onClick={() => onSort(sk)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <svg
            className={`h-3 w-3 transition-transform ${asc ? "" : "rotate-180"}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.707a1 1 0 011.414 0L10 11l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
    </th>
  );
}

// ─── Haulier Pricing Report Tab ──────────────────────────────────────────────

type HaulierFilter = "ALL" | "ISSUES" | "CORRECT" | "DUPLICATE" | "RATE_NOT_FOUND" | "RETURNS";

type HaulierSortKey =
  | "invoice_number"
  | "delivery_date"
  | "consignment_number"
  | "destination_name"
  | "destination_postcode"
  | "service_type"
  | "pallet_spec"
  | "pallet_quantity"
  | "invoiced_amount"
  | "expected_amount"
  | "amount_difference"
  | "discount_amount"
  | "status"
  | "is_return";

const HAULIER_ISSUE_STATUSES = new Set([
  "OVERCHARGED",
  "UNDERCHARGED",
  "RATE_NOT_FOUND",
  "UNKNOWN_SERVICE",
]);

function matchesHaulierFilter(
  row: HaulierReportLine,
  filter: HaulierFilter,
): boolean {
  switch (filter) {
    case "ALL":
      return true;
    case "ISSUES":
      return HAULIER_ISSUE_STATUSES.has(row.status);
    case "CORRECT":
      return row.status === "CORRECT";
    case "DUPLICATE":
      return row.status === "DUPLICATE";
    case "RATE_NOT_FOUND":
      return row.status === "RATE_NOT_FOUND";
    case "RETURNS":
      return row.is_return === true;
  }
}

function compareHaulierLines(
  a: HaulierReportLine,
  b: HaulierReportLine,
  key: HaulierSortKey,
  asc: boolean,
): number {
  let cmp: number;
  const av = a[key];
  const bv = b[key];
  if (typeof av === "number" && typeof bv === "number") {
    cmp = av - bv;
  } else if (typeof av === "boolean" && typeof bv === "boolean") {
    cmp = (av ? 1 : 0) - (bv ? 1 : 0);
  } else {
    cmp = String(av ?? "").localeCompare(String(bv ?? ""));
  }
  return asc ? cmp : -cmp;
}

const HAULIER_STATUS_COLORS: Record<string, string> = {
  CORRECT: "bg-green-100 text-green-800",
  OVERCHARGED: "bg-red-100 text-red-800",
  UNDERCHARGED: "bg-amber-100 text-amber-800",
  RATE_NOT_FOUND: "bg-gray-100 text-gray-800",
  UNKNOWN_SERVICE: "bg-gray-100 text-gray-800",
  DUPLICATE: "bg-purple-100 text-purple-800",
};

function HaulierStatusBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, " ");
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${HAULIER_STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      {label}
    </span>
  );
}

const HAULIER_PAGE_SIZE = 50;

function exportHaulierLinesCsv(
  lines: HaulierReportLine[],
  from: string,
  to: string,
): void {
  const rows: string[] = [];

  rows.push("HAULIER PRICING REPORT");
  rows.push(toCsvRow(["Period", `${from} - ${to}`]));
  rows.push(toCsvRow(["Total Lines", lines.length]));
  rows.push("");

  rows.push(
    toCsvRow([
      "Invoice #",
      "Date",
      "Con No",
      "Destination",
      "Postcode",
      "Service",
      "Pallet Size",
      "Qty",
      "Invoiced",
      "Expected",
      "Difference",
      "Discount",
      "Status",
      "Return",
    ]),
  );

  for (const line of lines) {
    rows.push(
      toCsvRow([
        line.invoice_number,
        line.delivery_date,
        line.consignment_number,
        line.destination_name,
        line.destination_postcode,
        line.service_type,
        line.pallet_spec,
        line.pallet_quantity,
        line.invoiced_amount.toFixed(2),
        line.expected_amount.toFixed(2),
        line.amount_difference.toFixed(2),
        line.discount_amount.toFixed(2),
        line.status,
        line.is_return ? "Yes" : "No",
      ]),
    );
  }

  const BOM = "\uFEFF";
  const csv = BOM + rows.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `haulier_pricing_report_${from}_${to}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function HaulierPricingTab() {
  const now = new Date();
  const [fromDate, setFromDate] = useState(toDateStr(startOfMonth(now)));
  const [toDate, setToDate] = useState(toDateStr(endOfMonth(now)));

  const [activeFrom, setActiveFrom] = useState("");
  const [activeTo, setActiveTo] = useState("");

  const [sortKey, setSortKey] = useState<HaulierSortKey>("delivery_date");
  const [sortAsc, setSortAsc] = useState(false);
  const [filter, setFilter] = useState<HaulierFilter>("ALL");
  const [page, setPage] = useState(0);

  const enabled = !!activeFrom && !!activeTo;

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<{ lines: HaulierReportLine[]; total_count: number }>({
    queryKey: ["reports", "haulier-lines", activeFrom, activeTo],
    queryFn: () => reports.haulierLines(activeFrom, activeTo),
    enabled,
  });

  const handleGenerate = useCallback(() => {
    setActiveFrom(fromDate);
    setActiveTo(toDate);
    setPage(0);
  }, [fromDate, toDate]);

  const handlePreset = useCallback((preset: RangePreset) => {
    const range = presetRange(preset);
    setFromDate(range.from);
    setToDate(range.to);
    setActiveFrom(range.from);
    setActiveTo(range.to);
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (key: HaulierSortKey) => {
      if (key === sortKey) {
        setSortAsc(!sortAsc);
      } else {
        setSortKey(key);
        setSortAsc(true);
      }
    },
    [sortKey, sortAsc],
  );

  const handleFilter = useCallback((f: HaulierFilter) => {
    setFilter(f);
    setPage(0);
  }, []);

  const lines = data?.lines ?? [];

  const filtered = useMemo(
    () => lines.filter((row) => matchesHaulierFilter(row, filter)),
    [lines, filter],
  );

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => compareHaulierLines(a, b, sortKey, sortAsc)),
    [filtered, sortKey, sortAsc],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / HAULIER_PAGE_SIZE));
  const paginated = sorted.slice(
    page * HAULIER_PAGE_SIZE,
    (page + 1) * HAULIER_PAGE_SIZE,
  );

  // Summary stats computed from the full (unfiltered) line set
  const stats = useMemo(() => {
    let totalInvoiced = 0;
    let totalExpected = 0;
    let totalDifference = 0;
    for (const l of lines) {
      totalInvoiced += l.invoiced_amount;
      totalExpected += l.expected_amount;
      totalDifference += l.amount_difference;
    }
    return {
      totalLines: lines.length,
      totalInvoiced,
      totalExpected,
      totalDifference,
    };
  }, [lines]);

  const FILTER_TABS: { value: HaulierFilter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: "ISSUES", label: "Issues Only" },
    { value: "CORRECT", label: "Correct" },
    { value: "DUPLICATE", label: "Duplicates" },
    { value: "RATE_NOT_FOUND", label: "Rate Not Found" },
    { value: "RETURNS", label: "Returns" },
  ];

  return (
    <div className="space-y-6">
      {/* ── Date Range Selector ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Select Period
        </h2>

        <div className="mb-4 flex flex-wrap gap-2">
          {(
            [
              ["this_month", "This Month"],
              ["last_month", "Last Month"],
              ["last_3_months", "Last 3 Months"],
              ["last_6_months", "Last 6 Months"],
              ["year_to_date", "Year to Date"],
            ] as [RangePreset, string][]
          ).map(([preset, label]) => (
            <button
              key={preset}
              onClick={() => handlePreset(preset)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label
              htmlFor="haulier-from-date"
              className="block text-sm font-medium text-gray-700"
            >
              From
            </label>
            <input
              id="haulier-from-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label
              htmlFor="haulier-to-date"
              className="block text-sm font-medium text-gray-700"
            >
              To
            </label>
            <input
              id="haulier-to-date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <button
            onClick={handleGenerate}
            disabled={!fromDate || !toDate}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Fetch Lines
          </button>
        </div>
      </div>

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <svg
            className="h-6 w-6 animate-spin text-emerald-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="ml-2 text-sm text-gray-500">
            Fetching haulier line items...
          </span>
        </div>
      )}

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to fetch haulier lines.{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {/* ── Results ──────────────────────────────────────────────────────── */}
      {data && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Haulier Line Items
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {formatDate(activeFrom)} &ndash; {formatDate(activeTo)}
                </p>
              </div>
              <button
                onClick={() =>
                  exportHaulierLinesCsv(sorted, activeFrom, activeTo)
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                  />
                </svg>
                Export CSV
              </button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <MiniStat
                label="Total Lines"
                value={stats.totalLines.toLocaleString()}
              />
              <MiniStat
                label="Total Invoiced"
                value={formatCurrency(stats.totalInvoiced)}
                mono
              />
              <MiniStat
                label="Total Expected"
                value={formatCurrency(stats.totalExpected)}
                mono
              />
              <MiniStat
                label="Total Difference"
                value={formatCurrency(stats.totalDifference)}
                mono
                highlight={Math.abs(stats.totalDifference) >= 0.01}
              />
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleFilter(tab.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === tab.value
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
            <span className="ml-auto self-center text-sm text-gray-500">
              {filtered.length} row{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Table */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <HaulierSortHeader label="Invoice #" sortKey="invoice_number" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Date" sortKey="delivery_date" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Con No" sortKey="consignment_number" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Destination" sortKey="destination_name" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Postcode" sortKey="destination_postcode" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Service" sortKey="service_type" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Pallet Size" sortKey="pallet_spec" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Qty" sortKey="pallet_quantity" currentKey={sortKey} asc={sortAsc} onSort={handleSort} align="right" />
                    <HaulierSortHeader label="Invoiced" sortKey="invoiced_amount" currentKey={sortKey} asc={sortAsc} onSort={handleSort} align="right" />
                    <HaulierSortHeader label="Expected" sortKey="expected_amount" currentKey={sortKey} asc={sortAsc} onSort={handleSort} align="right" />
                    <HaulierSortHeader label="Difference" sortKey="amount_difference" currentKey={sortKey} asc={sortAsc} onSort={handleSort} align="right" />
                    <HaulierSortHeader label="Discount" sortKey="discount_amount" currentKey={sortKey} asc={sortAsc} onSort={handleSort} align="right" />
                    <HaulierSortHeader label="Status" sortKey="status" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <HaulierSortHeader label="Return" sortKey="is_return" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {paginated.length === 0 ? (
                    <tr>
                      <td
                        colSpan={14}
                        className="px-3 py-8 text-center text-gray-500"
                      >
                        No line items match the current filter.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((line) => (
                      <tr
                        key={line.id}
                        className={`hover:bg-gray-50 ${
                          line.is_return
                            ? "bg-amber-50 border-l-2 border-l-amber-400"
                            : ""
                        }`}
                      >
                        <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                          {line.invoice_number || "-"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {line.delivery_date}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          <span className="inline-flex items-center gap-1.5">
                            {line.consignment_number}
                            {line.is_return && (
                              <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-amber-700">
                                RTN
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {line.destination_name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {line.destination_postcode}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {line.service_type}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {line.pallet_spec}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-900">
                          {line.pallet_quantity}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-900">
                          {formatCurrency(line.invoiced_amount)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-900">
                          {formatCurrency(line.expected_amount)}
                        </td>
                        <td
                          className={`whitespace-nowrap px-3 py-2 text-right font-mono font-semibold ${
                            Math.abs(line.amount_difference) >= 0.01
                              ? line.amount_difference > 0
                                ? "text-red-600"
                                : "text-green-600"
                              : "text-green-600"
                          }`}
                        >
                          {Math.abs(line.amount_difference) < 0.01
                            ? "Match"
                            : formatCurrency(line.amount_difference)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-700">
                          {line.discount_amount > 0
                            ? formatCurrency(line.discount_amount)
                            : "-"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <HaulierStatusBadge status={line.status} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                          {line.is_return ? "Yes" : "No"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {lines.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
              <p className="text-sm text-gray-500">
                No haulier line items found in this period.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HaulierSortHeader({
  label,
  sortKey: sk,
  currentKey,
  asc,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: HaulierSortKey;
  currentKey: HaulierSortKey;
  asc: boolean;
  onSort: (key: HaulierSortKey) => void;
  align?: "left" | "right";
}) {
  const isActive = sk === currentKey;
  return (
    <th
      className={`cursor-pointer select-none whitespace-nowrap px-3 py-2.5 transition-colors hover:text-gray-700 ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={() => onSort(sk)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <svg
            className={`h-3 w-3 transition-transform ${asc ? "" : "rotate-180"}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.707a1 1 0 011.414 0L10 11l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
    </th>
  );
}

// ─── Zone Breakdown (existing) ──────────────────────────────────────────────

function ZoneBreakdownSection({ zones }: { zones: ZoneSummary[] }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Zone Breakdown (Aggregated)
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-6 py-3">Zone</th>
              <th className="px-6 py-3">Lines</th>
              <th className="px-6 py-3 text-right">Invoiced</th>
              <th className="px-6 py-3 text-right">Expected</th>
              <th className="px-6 py-3 text-right">Difference</th>
            </tr>
          </thead>
          <tbody>
            {zones.map((zone) => (
              <ZoneRow key={zone.zone} zone={zone} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-300 bg-gray-50 font-semibold text-gray-900">
              <td className="px-6 py-3">Total</td>
              <td className="px-6 py-3">
                {zones.reduce((s, z) => s + z.lines, 0)}
              </td>
              <td className="px-6 py-3 text-right font-mono">
                {formatCurrency(
                  zones.reduce((s, z) => s + z.total_invoiced, 0),
                )}
              </td>
              <td className="px-6 py-3 text-right font-mono">
                {formatCurrency(
                  zones.reduce((s, z) => s + z.total_expected, 0),
                )}
              </td>
              <td
                className={`px-6 py-3 text-right font-mono ${
                  zones.reduce((s, z) => s + z.difference, 0) > 0
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {formatCurrency(
                  zones.reduce((s, z) => s + z.difference, 0),
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function ZoneRow({ zone }: { zone: ZoneSummary }) {
  const hasDiff = Math.abs(zone.difference) >= 0.01;

  return (
    <>
      <tr className="border-b border-gray-100">
        <td className="px-6 py-3 font-medium text-gray-900">
          Zone {zone.zone}
        </td>
        <td className="px-6 py-3 text-gray-600">{zone.lines}</td>
        <td className="px-6 py-3 text-right font-mono text-gray-900">
          {formatCurrency(zone.total_invoiced)}
        </td>
        <td className="px-6 py-3 text-right font-mono text-gray-900">
          {formatCurrency(zone.total_expected)}
        </td>
        <td
          className={`px-6 py-3 text-right font-mono font-semibold ${
            hasDiff ? "text-red-600" : "text-green-600"
          }`}
        >
          {hasDiff ? formatCurrency(zone.difference) : "Match"}
        </td>
      </tr>
      {zone.pallet_breakdown.map((pb) => (
        <tr
          key={`${zone.zone}-${pb.pallet_type}`}
          className="border-b border-gray-50 bg-gray-50/50 text-xs text-gray-500"
        >
          <td className="py-1.5 pl-12 pr-6">{pb.pallet_type}</td>
          <td className="px-6 py-1.5">{pb.count}</td>
          <td className="px-6 py-1.5 text-right font-mono">
            {formatCurrency(pb.total_invoiced)}
          </td>
          <td className="px-6 py-1.5 text-right font-mono">
            {formatCurrency(pb.total_expected)}
          </td>
          <td
            className={`px-6 py-1.5 text-right font-mono ${
              Math.abs(pb.per_unit_difference) >= 0.01
                ? "text-red-500"
                : "text-green-500"
            }`}
          >
            {Math.abs(pb.per_unit_difference) >= 0.01
              ? `${formatCurrency(pb.per_unit_difference)}/pallet`
              : "Match"}
          </td>
        </tr>
      ))}
    </>
  );
}
